'use client';

import { motion, AnimatePresence } from 'framer-motion';

interface GameOverModalProps {
  word: string;
  onClose?: () => void;
  isHardcore?: boolean;
  strings: {
    title: string;
    wordWas: string;
    subline: string;
    closeLabel: string;
  };
}

export function GameOverModal({
  word,
  onClose,
  isHardcore = false,
  strings,
}: GameOverModalProps) {
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        <motion.div
          className={`relative w-full max-w-sm overflow-hidden rounded-2xl p-6 text-center text-white shadow-2xl ring-1 ${
            isHardcore
              ? 'bg-gradient-to-b from-red-950 via-neutral-950 to-neutral-950 ring-red-500/40 shadow-[0_0_70px_rgba(239,68,68,0.30)]'
              : 'bg-gradient-to-b from-neutral-800 to-neutral-950 ring-white/10'
          }`}
          initial={{ scale: 0.85, y: 40, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        >
          {/* Halo décoratif */}
          <div
            className={`pointer-events-none absolute -top-20 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full blur-3xl ${
              isHardcore ? 'bg-red-600/30' : 'bg-white/10'
            }`}
          />

          <motion.h2
            className="relative mb-5 text-3xl font-extrabold tracking-tight"
            initial={{ rotate: -8, scale: 0.4, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            transition={{
              type: 'spring',
              delay: 0.1,
              stiffness: 240,
              damping: 15,
            }}
          >
            {strings.title}
          </motion.h2>

          <div className="relative flex flex-col items-center gap-2">
            <span
              className={`text-sm text-neutral-300 ${
                isHardcore ? 'font-bold' : ''
              }`}
            >
              {strings.wordWas}
            </span>
            <span
              className={`rounded-full px-4 py-1.5 font-mono text-lg font-bold uppercase tracking-[0.25em] ${
                isHardcore
                  ? 'bg-red-500/15 text-red-300 ring-1 ring-red-500/40'
                  : 'bg-white/10 text-white ring-1 ring-white/15'
              }`}
            >
              {word}
            </span>
          </div>

          <p
            className={`relative mt-4 text-xs text-neutral-400 ${
              isHardcore ? 'font-bold' : ''
            }`}
          >
            {strings.subline}
          </p>

          <button
            type="button"
            onClick={onClose}
            className={`relative mt-6 w-full rounded-lg py-2.5 text-sm font-bold transition active:scale-95 ${
              isHardcore
                ? 'bg-red-600 text-white shadow-[0_8px_24px_rgba(239,68,68,0.35)] hover:bg-red-500'
                : 'bg-neutral-700 text-white hover:bg-neutral-600'
            }`}
          >
            {strings.closeLabel}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
